'use client'

import * as React from 'react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { Check } from 'iconoir-react'
import { entries } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { ComboInputSearch } from './combo-input-search'
import { CustomOption } from 'app/components/shared/styled/CustomOption'
import { cn } from 'app/lib/utils'
import { debug } from 'lib/logging'
import { OptionType } from 'types/OptionType'

type Props = {
  options?: OptionType[]
  open: boolean
  showSearchInDropdown?: boolean
  value?: OptionType
  onClearSearch?: () => void
  onSelect: (option: OptionType) => void
}

export function GroupedComboOptionsView({
  options,
  open,
  showSearchInDropdown = true,
  value,
  onClearSearch,
  onSelect,
}: Props) {
  const groups = useMemo(
    () =>
      options?.reduce(
        (groups, option) => {
          let group = option.meta?.group ?? `Other`
          if (groups[group] == null) {
            groups[group] = [option]
          } else {
            groups[group].push(option)
          }
          return groups
        },
        {} as Record<string, OptionType[]>,
      ),
    [options],
  )
  const [filteredOptions, setFilteredOptions] = useState<Record<string, OptionType[]> | undefined>(groups)
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({})

  const toggleGroup = (group: string) => {
    setOpenGroups((prev) => ({ ...prev, [group]: prev[group] === false ? true : false }))
  }

  const isGroupOpen = (group: string) => openGroups[group] !== false // default open

  const handleSearchValueChange = (text: string) => {
    if (text.length === 0) {
      setFilteredOptions(groups)
      return
    }
    setFilteredOptions(() => {
      let filtered = { ...groups }
      entries(filtered).forEach(([group, options]) => {
        filtered[group] = options.filter((option) => option.name.toLowerCase().includes(text.toLowerCase()))
        if (filtered[group].length === 0) {
          delete filtered[group]
        }
      })
      return filtered
    })
  }

  useEffect(() => {
    if (!open) setFilteredOptions(groups)
  }, [open])

  debug(`groups`, `combobox grouped options`, groups, `filtered grouped options`, filteredOptions)

  if (!open) return null

  return (
    <div
      className={cn(
        `flex h-full min-h-0 w-full flex-col overflow-hidden capitalize sm:text-body-xs sm:text-strong`,
        `cursor-pointer`,
      )}
      onWheel={(evt) => evt.stopPropagation()}
    >
      {showSearchInDropdown && (
        <ComboInputSearch
          handleSearchValueChange={handleSearchValueChange}
          onClearSearch={() => {
            setFilteredOptions(groups)
            onClearSearch?.()
          }}
        />
      )}
      <div className="combobox-grouped-scroll min-h-0 flex-1 overflow-y-auto">
        {entries(filteredOptions)?.map(([group, groupOptions], i) => (
          <div key={group}>
            <button
              type="button"
              onClick={() => toggleGroup(group)}
              className={cn(
                `flex w-full items-center justify-between py-2 pl-4 pr-[6px]`,
                i !== 0 ? `border-t border-default` : ``,
              )}
            >
              <span className="text-ui-sm text-strong">{group}</span>
              <ChevronDownIcon
                className={cn(`h-5 w-5`, groupOptions.length > 0 && isGroupOpen(group) ? `rotate-180 transform` : ``)}
              />
            </button>
            {isGroupOpen(group) &&
              groupOptions?.map((option) => (
                <div
                  className="h-8 text-body-sm leading-8 hover:bg-(--menu-hover) text-default"
                  key={`${option.id}-${option.name}-${option.value}`}
                >
                  <div
                    onClick={() => onSelect(option)}
                    className={cn(`cursor-pointer pl-4 pr-1.5 h-full flex items-center`)}
                  >
                    {option.meta?.customComboOption ? (
                      <CustomOption selected={value && value.id === option.id}>
                        {option.meta.customComboOption}
                      </CustomOption>
                    ) : (
                      <div className={cn(`flex w-full items-center justify-between`)}>
                        <span className={cn(`flex items-center whitespace-pre-wrap`)}>{option.name}</span>
                        <Check className={cn(`h-5 w-5`, value && value.id === option.id ? `ml-4 block` : `hidden`)} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </div>
        ))}
      </div>
    </div>
  )
}
